import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {type DraggableData, type DraggableEvent} from 'react-draggable';

import {useWBAgentContextStore} from './WBAgentContextStore';

const BOTTOM_OFFSET = 80;
const RIGHT_OFFSET = 24;

export function useBoundedDrag(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const position = useWBAgentContextStore(s => s.windowPosition);
  const setPosition = useWBAgentContextStore(s => s.setWindowPosition);

  const clampToContainer = useCallback(() => {
    const pos = useWBAgentContextStore.getState().windowPosition;
    if (!containerRef.current || !nodeRef.current || !pos) {
      return;
    }
    const {clientWidth: cw, clientHeight: ch} = containerRef.current;
    const {offsetWidth: nw, offsetHeight: nh} = nodeRef.current;

    const clamped = {
      x: Math.max(0, Math.min(pos.x, cw - nw)),
      y: Math.max(0, Math.min(pos.y, ch - nh)),
    };
    if (clamped.x !== pos.x || clamped.y !== pos.y) {
      setPosition(clamped);
    }
  }, [setPosition]);

  useLayoutEffect(() => {
    if (!active || !containerRef.current || !nodeRef.current) {
      return;
    }
    if (position != null) {
      clampToContainer();
      return;
    }
    const {clientWidth: cw, clientHeight: ch} = containerRef.current;
    const {offsetWidth: nw, offsetHeight: nh} = nodeRef.current;

    setPosition({
      x: Math.max(0, cw - RIGHT_OFFSET - nw),
      y: Math.max(0, ch - BOTTOM_OFFSET - nh),
    });
    // Disabling eslint here is justified here because otherwise position would have to be in this dep array,
    // and we don't want to trigger a rerender every time position changes.
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active) {
      return;
    }

    let rafId: number;
    const scheduleClamp = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => clampToContainer());
    };

    window.addEventListener('resize', scheduleClamp);

    const ro = new ResizeObserver(scheduleClamp);
    if (containerRef.current) {
      ro.observe(containerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleClamp);
      ro.disconnect();
    };
  }, [active, clampToContainer]);

  const handleDrag = (_e: DraggableEvent, data: DraggableData) => {
    setPosition({x: data.x, y: data.y});
  };

  return {
    containerRef,
    nodeRef,
    position: position ?? {x: 0, y: 0},
    handleDrag,
  };
}
